import Close20 from '@carbon/icons-react/es/close/20';
import { QueryClientProvider } from '@tanstack/react-query';
import { notification, Space } from 'antd';
import { FC, ReactNode } from 'react';
import { Provider } from 'react-redux';
import styled from 'styled-components';
import { TertiaryButton } from 'components/atoms/tertiary-button';
import {
  ProductiveHeading07,
  Text02,
  Text01,
  BodyShort01,
  ColFlexBox01,
} from 'components/styles';
import { store } from 'data';
import { queryClient } from 'queries';
import { CheckmarkIcon } from './checmark';
import { ErrorIcon } from './error';
import { InformationIcon } from './information';
import { WarningIcon } from './warning';

type ToastType = 'success' | 'error' | 'info' | 'ai';

const ToastTitle = styled.div`
  ${ProductiveHeading07};
  ${Text01};
`;

const ToastDesc = styled.div<{ type: ToastType }>`
  ${BodyShort01};
  ${ColFlexBox01};
  ${Text02};
  color: ${({ type }) => (type === 'ai' ? 'var(--white-100)' : 'inherit')};
`;

type Placement = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

// TODO: Change API
export const toastService = ({
  toastContainer,
  duration,
  title,
  message,
  placement = 'bottomLeft',
  key,
}: {
  toastContainer?: any;
  title?: React.ReactNode | string;
  message: React.ReactNode | string;
  placement?: Placement;
  key?: string;
  duration?: number;
}): {
  success(): void;
  error(): void;
  info(): void;
  aiError(): void;
} => {
  const config = (type: ToastType) => ({
    message: <ToastTitle data-cy="toast.title">{title}</ToastTitle>,
    description: (
      <ToastDesc data-cy="toast.description" type={type}>
        {message}
      </ToastDesc>
    ),
    placement,
    duration,
    getContainer: () => {
      return toastContainer?.current || document.body;
    },
    closeIcon: (
      <span data-cy="toast.close-icon">
        <Close20 color={type === 'ai' ? 'var(--white-100)' : 'inherit'} />
      </span>
    ),
    key,
  });

  const style = {
    success: {
      backgroundColor: 'var(--green-10)',
      borderLeft: '3px solid var(--green-50)',
    },
    error: {
      backgroundColor: 'var(--red-10)',
      borderLeft: '3px solid var(--red-50)',
    },
    info: {
      backgroundColor: 'var(--blue-10)',
      borderLeft: '3px solid var(--blue-50)',
    },
    ai: {
      backgroundColor: 'var(--warm-gray-90)',
      borderRadius: 'var(--border-radius-default)',
      color: 'var(--white-100)',
      alignItems: 'center',
    },
  };

  return {
    success: () => {
      return notification.success({
        ...config('success'),
        style: style.success,
        icon: <CheckmarkIcon />,
      });
    },
    error: () => {
      return notification.error({
        ...config('error'),
        style: style.error,
        icon: <ErrorIcon />,
      });
    },
    info: () => {
      return notification.info({
        ...config('info'),
        style: style.info,
        icon: <InformationIcon />,
      });
    },
    aiError: () => {
      return notification.error({
        ...config('ai'),
        style: style.ai,
        icon: <WarningIcon />,
      });
    },
  };
};

interface Props {
  message: ReactNode;
  onClick?: () => void;
  buttonText?: string;
}

/* Note:: Do not want to use brand color in button inside toast. It is decision taken by the designer.
@LinK:: https://northstar.nitro.run/#task_drawer=310.9582 */
const CTA = styled(TertiaryButton)`
  border: solid 1px var(--primary-color);
  color: var(--primary-color);
  box-shadow: var(--shadow-highlight)
    rgba(var(--blue-60-in-rgb), var(--shadow-highlight-color-opacity));

  &:focus,
  &:active,
  &:hover {
    color: var(--white-100);
    background-color: var(--primary-color) !important;
  }
`;

export const ToastDescription: FC<React.PropsWithChildren<Props>> = ({
  message = '',
  onClick,
  buttonText = '',
}) => {
  return (
    <Space direction="vertical">
      <div data-cy="toast.description.custom-message-element">{message}</div>
      {onClick && (
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <CTA data-cy="toast.action-btn" size="small" onClick={onClick}>
              {buttonText}
            </CTA>
          </Provider>
        </QueryClientProvider>
      )}
    </Space>
  );
};
